import * as React from "react";
import { createPlayTimeAccumulator } from "@/app/lib/media/playTimeAccumulator";
import type { PlayTimeProgress } from "@/app/lib/media/playTimeAccumulator";
import * as s from "../pages/CertificationContent.css";

const POLL_MS = 1000;

export type Html5LessonPlayerProps = {
  src: string;
  className?: string;
  initialWatchedSeconds?: number;
  onWatchProgress?: (progress: PlayTimeProgress) => void;
  onPersist?: () => void;
  onEnded?: () => void;
};

export function Html5LessonPlayer({
  src,
  className,
  initialWatchedSeconds = 0,
  onWatchProgress,
  onPersist,
  onEnded,
}: Html5LessonPlayerProps) {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const accumRef = React.useRef(createPlayTimeAccumulator(POLL_MS / 1000));
  const onWatchProgressRef = React.useRef(onWatchProgress);
  onWatchProgressRef.current = onWatchProgress;
  const onPersistRef = React.useRef(onPersist);
  onPersistRef.current = onPersist;
  const onEndedRef = React.useRef(onEnded);
  onEndedRef.current = onEnded;

  React.useEffect(() => {
    accumRef.current = createPlayTimeAccumulator(POLL_MS / 1000);
    accumRef.current.seed(initialWatchedSeconds);
  }, [src]);

  React.useEffect(() => {
    accumRef.current.seed(initialWatchedSeconds);
  }, [initialWatchedSeconds]);

  const emit = React.useCallback((video: HTMLVideoElement) => {
    const progress = accumRef.current.tick({
      currentTime: video.currentTime,
      duration: video.duration,
      playbackRate: video.playbackRate,
    });
    onWatchProgressRef.current?.(progress);
  }, []);

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let interval: number | null = null;
    const start = () => {
      if (interval != null) return;
      interval = window.setInterval(() => emit(video), POLL_MS);
    };
    const stop = () => {
      if (interval == null) return;
      window.clearInterval(interval);
      interval = null;
    };

    const onPlaying = () => {
      accumRef.current.markPosition(video.currentTime);
      start();
    };
    const onPause = () => {
      emit(video);
      stop();
      onPersistRef.current?.();
    };
    const onEndedEvent = () => {
      emit(video);
      stop();
      onPersistRef.current?.();
      onEndedRef.current?.();
    };
    const onSeeked = () => {
      accumRef.current.markPosition(video.currentTime);
    };

    video.addEventListener("playing", onPlaying);
    video.addEventListener("pause", onPause);
    video.addEventListener("ended", onEndedEvent);
    video.addEventListener("seeked", onSeeked);
    return () => {
      stop();
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("ended", onEndedEvent);
      video.removeEventListener("seeked", onSeeked);
    };
  }, [emit, src]);

  return (
    <video
      ref={videoRef}
      src={src}
      controls
      className={className ?? s.quizVideoPlayer}
    />
  );
}
